import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import MediaView from "@/components/MediaView";
import AvatarUploader from "@/components/AvatarUploader";
import AdminMembersPanel from "@/components/AdminMembersPanel";
import AdminReportsPanel from "@/components/AdminReportsPanel";
import AdminElderReportsPanel from "@/components/AdminElderReportsPanel";
import AdminWaitlistPanel from "@/components/AdminWaitlistPanel";
import AdminBookQueue from "@/components/AdminBookQueue";
import AdminDigestPanel from "@/components/AdminDigestPanel";
import KingdomSupporterBadge from "@/components/KingdomSupporterBadge";
import YearInTheLord from "@/components/YearInTheLord";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import SecurityQuestionsCard from "@/components/SecurityQuestionsCard";
import EmailPreferencesCard from "@/components/EmailPreferencesCard";
import { PROFILE_TYPES, profileTypeMap } from "@/lib/profileTypes";
import { Trash2, MessageCircle, HandHeart, Quote, Image as ImageIcon, MessageSquare, Pencil, Check, X, MousePointerClick, UserPlus, Trophy, Building2, Church, HandHeart as HeartIcon, MapPin } from "lucide-react";
import { toast } from "sonner";

const DIR_ICONS = { church: Church, business: Building2, ministry: HeartIcon };
const STATUS_BADGE = {
  approved: "bg-gold/15 text-burgundy",
  pending: "bg-burgundy/10 text-burgundy",
  rejected: "bg-cream-surface text-jjblue/60",
};

const TABS = [
  { key: "posts", label: "Posts", icon: MessageCircle },
  { key: "prayers", label: "Prayer Requests", icon: HandHeart },
  { key: "memories", label: "Memories", icon: ImageIcon },
  { key: "testimonies", label: "Testimonies", icon: Quote },
  { key: "comments", label: "Comments", icon: MessageSquare },
];

function Empty({ label }) {
  return <div className="text-center text-[var(--jj-muted)] py-10">No {label.toLowerCase()} yet.</div>;
}

function AdminAwardsRunner() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-monthly-awards?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to award yet");
    } finally { setBusy(false); }
  };

  const confirmAward = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-monthly-awards");
      toast.success(`${data.winners.length} member(s) honored for ${data.month}`);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run awards");
    } finally { setBusy(false); }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-awards-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5" /> Admin · Monthly Awards
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1">Honor last month&rsquo;s top inviters.</h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Auto-upgrades the top 3 Seekers from last month to <span className="text-burgundy font-medium">Believer</span>, and posts a celebratory testimony on the wall. Runs once per month — safe to preview first.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runDryRun} disabled={busy} className="btn-secondary !py-2 !px-4 text-sm" data-testid="admin-awards-preview">
            {busy ? "Loading…" : "Preview winners"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-5 jj-card p-5 bg-white" data-testid="admin-awards-preview-card">
          <div className="text-xs uppercase tracking-wider text-burgundy mb-3">{preview.month} preview</div>
          {preview.winners.length === 0 ? (
            <div className="text-sm text-[var(--jj-muted)]">No attributed signups in this window.</div>
          ) : (
            <>
              <ol className="space-y-2">
                {preview.winners.map((w, i) => (
                  <li key={w.id} className="flex items-center gap-3 text-sm" data-testid={`admin-award-preview-${w.id}`}>
                    <span className="font-serif text-lg w-6 text-center">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i+1}`}</span>
                    <span className="font-medium text-jjblue">{w.name}</span>
                    <span className="text-[var(--jj-muted)]">— {w.signups} signups</span>
                    {w.tier_before === "Seeker" && (
                      <span className="ml-auto text-[11px] bg-gold/20 text-burgundy px-2 py-0.5 rounded-full">→ Believer</span>
                    )}
                  </li>
                ))}
              </ol>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button onClick={() => setPreview(null)} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
                <button onClick={confirmAward} disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="admin-awards-confirm">
                  {busy ? "Honoring…" : "Honor these members"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MyDirectoryListings() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/directory?mine=true&limit=50")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="jj-card mt-6 p-6 sm:p-7" data-testid="my-listings-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-burgundy flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> My Directory Listings
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1">Places you&rsquo;ve submitted.</h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)] max-w-md">
            Track the status of every listing &mdash; pending submissions are visible only to you and admins until approved.
          </p>
        </div>
        <Link to="/directory/submit" className="btn-secondary !py-2 !px-4 text-sm" data-testid="my-listings-add">+ Submit another</Link>
      </div>
      <ul className="mt-5 space-y-2">
        {items.map((d) => {
          const Icon = DIR_ICONS[d.kind] || Building2;
          const cls = STATUS_BADGE[d.status] || STATUS_BADGE.pending;
          return (
            <li key={d.id} data-testid={`my-listing-${d.id}`}>
              <Link to={`/directory/${d.id}`} className="flex items-center gap-3 jj-card p-4 hover:border-gold transition-colors">
                <div className="h-9 w-9 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-jjblue truncate">{d.name}</div>
                  <div className="mt-0.5 text-xs text-[var(--jj-muted)] flex items-center gap-2 flex-wrap">
                    <span className="capitalize">{d.kind}</span>
                    {(d.city || d.state) && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[d.city, d.state].filter(Boolean).join(", ")}</span>
                    )}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${cls}`}>{d.status}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AdminModerationInbox() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/directory?status=pending&limit=30");
      setPending(data || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/directory/${id}/approve`);
      toast.success("Approved");
      setPending((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Could not approve"); }
  };

  const reject = async (id, name) => {
    if (!window.confirm(`Reject and remove "${name}"?`)) return;
    try {
      await api.delete(`/directory/${id}`);
      toast.success("Removed");
      setPending((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Could not remove"); }
  };

  if (loading) return null;

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-moderation-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-burgundy flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5" /> Admin &middot; Moderation Inbox
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1">
            {pending.length} listing{pending.length === 1 ? "" : "s"} awaiting review.
          </h2>
        </div>
        {pending.length > 0 && (
          <span className="text-xs text-[var(--jj-muted)]">Approve to publish &middot; Reject to delete</span>
        )}
      </div>

      {pending.length === 0 ? (
        <div className="mt-4 text-sm text-[var(--jj-muted)]">All caught up. The queue is empty.</div>
      ) : (
        <ul className="mt-5 space-y-2">
          {pending.map((d) => {
            const Icon = DIR_ICONS[d.kind] || Building2;
            return (
              <li key={d.id} className="jj-card bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`moderation-item-${d.id}`}>
                <div className="h-9 w-9 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/directory/${d.id}`} className="font-medium text-jjblue hover:text-burgundy hover:underline truncate block">{d.name}</Link>
                  <div className="mt-0.5 text-xs text-[var(--jj-muted)] flex items-center gap-2 flex-wrap">
                    <span className="capitalize">{d.kind}</span>
                    {(d.city || d.state) && (<span>&middot; {[d.city, d.state].filter(Boolean).join(", ")}</span>)}
                    {d.owner_name && (<span>&middot; by <Link to={`/u/${d.owner_user_id}`} className="text-jjblue hover:text-burgundy hover:underline">{d.owner_name}</Link></span>)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => approve(d.id)} className="btn-gold !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`moderation-approve-${d.id}`}>
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button onClick={() => reject(d.id, d.name)} className="text-xs text-burgundy hover:text-[var(--jj-burgundy-hover)] inline-flex items-center gap-1" data-testid={`moderation-reject-${d.id}`}>
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function BioEditor({ user, setUser }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.bio || "");
  const [busy, setBusy] = useState(false);
  const limit = 500;

  const save = async () => {
    if (draft === (user.bio || "")) { setEditing(false); return; }
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { bio: draft.trim() });
      setUser(data);
      toast.success("Bio saved");
      setEditing(false);
    } catch {
      toast.error("Could not save bio");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(user.bio || "");
    setEditing(false);
  };

  if (!editing) {
    if (!user.bio) {
      return (
        <div className="mt-6 p-4 rounded-xl border border-dashed border-jj text-sm flex items-center justify-between gap-3" data-testid="bio-empty">
          <span className="text-[var(--jj-muted)]">No bio yet &mdash; share a sentence so the family knows you.</span>
          <button onClick={() => setEditing(true)} className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="bio-add">
            <Pencil className="h-3 w-3" /> Add bio
          </button>
        </div>
      );
    }
    return (
      <div className="mt-6 group relative" data-testid="bio-display">
        <p className="text-jjblue/85 leading-relaxed whitespace-pre-wrap pr-10">{user.bio}</p>
        <button
          onClick={() => setEditing(true)}
          className="absolute top-0 right-0 text-[var(--jj-muted)] hover:text-burgundy opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Edit bio"
          data-testid="bio-edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="bio-editor">
      <label className="overline text-burgundy">Your bio</label>
      <textarea
        autoFocus
        rows={3}
        maxLength={limit}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="A sentence about you. What you love. What you&rsquo;re praying for. How the Lord found you."
        className="input-jj mt-2"
        data-testid="bio-textarea"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[var(--jj-muted)]">{draft.length}/{limit}</span>
        <div className="flex items-center gap-2">
          <button onClick={cancel} className="btn-secondary !py-1.5 !px-3 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="bio-save">
            <Check className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("posts");
  const [data, setData] = useState(null);
  const [editingType, setEditingType] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [invites, setInvites] = useState(null);

  const load = async () => {
    const { data } = await api.get("/me/contributions");
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/me/invites").then(({ data }) => setInvites(data)).catch(() => {});
  }, []);

  const saveProfileType = async (key) => {
    setSavingType(true);
    try {
      const { data } = await api.patch("/auth/me", { profile_type: key });
      setUser(data);
      setEditingType(false);
      toast.success("Identity updated");
    } catch {
      toast.error("Could not update");
    } finally {
      setSavingType(false);
    }
  };

  const del = async (kind, id, endpoint) => {
    if (!window.confirm("Delete this? This can't be undone.")) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      setData({ ...data, [kind]: data[kind].filter((x) => x.id !== id) });
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  if (!user) return null;

  const currentType = user.profile_type ? profileTypeMap[user.profile_type] : null;

  const counts = data || {};
  const list = data?.[tab] || [];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="jj-card p-8 sm:p-10 fade-up" data-testid="profile-card">
        <div className="flex items-center gap-5">
          <AvatarUploader user={user} setUser={setUser} size={72} />
          <div className="flex-1">
            <h1 className="font-serif text-4xl text-jjblue">{user.name}</h1>
            <div className="text-sm text-[var(--jj-muted)]">{user.email}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {currentType && !editingType && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-surface text-jjblue text-xs">
                  <currentType.icon className="h-3.5 w-3.5 text-burgundy" strokeWidth={1.7} />
                  <span className="font-medium">{currentType.label}</span>
                  <button onClick={() => setEditingType(true)} className="text-[var(--jj-muted)] hover:text-burgundy ml-1" data-testid="profile-type-edit">
                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </div>
              )}
              {user.kingdom_supporter && (
                <KingdomSupporterBadge since={user.supporter_since} />
              )}
            </div>
          </div>
          <Link to="/pricing" className="btn-secondary !py-2 !px-4 text-sm" data-testid="profile-upgrade">Manage membership</Link>
        </div>

        {/* "Who are you?" — prompt for unset OR inline editor */}
        {(editingType || !currentType) && (
          <div className="mt-6 p-5 rounded-xl bg-cream-surface" data-testid="profile-type-picker">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline text-burgundy">Who are you?</div>
                <p className="text-sm text-[var(--jj-text-secondary)] mt-1">
                  Help the community know how to connect with you. You can change this anytime.
                </p>
              </div>
              {editingType && (
                <button onClick={() => setEditingType(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Cancel">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {PROFILE_TYPES.map((t) => {
                const Icon = t.icon;
                const selected = user.profile_type === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => saveProfileType(t.key)}
                    disabled={savingType}
                    data-testid={`profile-type-pick-${t.key}`}
                    className={`text-left p-3 rounded-lg border transition-all ${
                      selected ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)]" : "bg-white border-jj hover:border-[var(--jj-gold)]"
                    }`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${selected ? "text-cream" : "text-burgundy"}`} strokeWidth={1.7} />
                      <span className={`text-sm font-medium ${selected ? "text-cream" : "text-jjblue"}`}>{t.label}</span>
                      {selected && <Check className="h-3 w-3 text-cream ml-auto" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <BioEditor user={user} setUser={setUser} />
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-cream-surface rounded-xl p-4">
            <div className="overline text-burgundy">Tier</div>
            <div className="font-serif text-2xl text-gold mt-1">{user.tier}</div>
          </div>
          <div className="bg-cream-surface rounded-xl p-4">
            <div className="overline text-burgundy">Posts</div>
            <div className="font-serif text-2xl text-jjblue mt-1">{counts.posts?.length ?? "—"}</div>
          </div>
          <div className="bg-cream-surface rounded-xl p-4">
            <div className="overline text-burgundy">Prayers</div>
            <div className="font-serif text-2xl text-jjblue mt-1">{counts.prayers?.length ?? "—"}</div>
          </div>
          <div className="bg-cream-surface rounded-xl p-4">
            <div className="overline text-burgundy">Testimonies</div>
            <div className="font-serif text-2xl text-jjblue mt-1">{counts.testimonies?.length ?? "—"}</div>
          </div>
        </div>
      </div>

      {/* Personal invite scoreboard */}
      {invites && (invites.clicks > 0 || invites.signups > 0) && (
        <div className="jj-card mt-6 p-6 sm:p-7 fade-up" data-testid="my-invites-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline text-burgundy">Your Invites</div>
              <h2 className="font-serif text-2xl text-jjblue mt-1">Carrying the family in.</h2>
              <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)] max-w-md">Stats from every share link you&rsquo;ve sent — updates instantly when a friend opens your invite or signs up.</p>
              {invites.awarded_at && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs bg-gold/15 text-burgundy px-3 py-1 rounded-full">
                  <Trophy className="h-3.5 w-3.5" /> Honored on {new Date(invites.awarded_at).toLocaleDateString()}
                </div>
              )}
            </div>
            <Link to="/cinema" className="btn-secondary !py-2 !px-4 text-sm" data-testid="my-invites-share-cta">Share another</Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="bg-cream-surface rounded-xl p-4 flex items-center gap-3">
              <MousePointerClick className="h-7 w-7 text-burgundy" strokeWidth={1.4} />
              <div>
                <div className="font-serif text-3xl text-jjblue leading-none">{invites.clicks}</div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)] mt-1">Link opens</div>
              </div>
            </div>
            <div className="bg-cream-surface rounded-xl p-4 flex items-center gap-3">
              <UserPlus className="h-7 w-7 text-burgundy" strokeWidth={1.4} />
              <div>
                <div className="font-serif text-3xl text-gold leading-none">{invites.signups}</div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)] mt-1">Signed up</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {user.role === "admin" && <AdminMembersPanel currentUserId={user.id} />}
      {user.kingdom_supporter && (
        <KingdomSupporterBadge size="lg" since={user.supporter_since} ordersCount={user.shop_orders_count} />
      )}
      <ChangePasswordCard />
      <SecurityQuestionsCard />
      <EmailPreferencesCard />
      {user.role === "admin" && <AdminReportsPanel />}
      {user.role === "admin" && <AdminElderReportsPanel />}
      {user.role === "admin" && <AdminBookQueue />}
      {user.role === "admin" && <AdminDigestPanel />}
      {user.role === "admin" && <AdminWaitlistPanel />}
      {user.role === "admin" && <AdminAwardsRunner />}
      {user.role === "admin" && <AdminModerationInbox />}
      <YearInTheLord />
      <MyDirectoryListings />

      <div className="mt-8">
        <div className="overline text-burgundy">My Contributions</div>
        <h2 className="font-serif text-3xl text-jjblue mt-2">Everything you've shared</h2>

        <div className="mt-5 flex gap-2 flex-wrap border-b border-jj">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${
                tab === t.key ? "border-burgundy text-burgundy" : "border-transparent text-jjblue/70 hover:text-burgundy"
              }`}
              data-testid={`profile-tab-${t.key}`}>
              <t.icon className="h-4 w-4" strokeWidth={1.6} /> {t.label}
              {data?.[t.key] && <span className="text-xs text-[var(--jj-muted)]">{data[t.key].length}</span>}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {!data && <div className="text-[var(--jj-muted)] text-center py-10">Loading…</div>}

          {data && tab === "posts" && (list.length === 0 ? <Empty label="Posts" /> : list.map((p) => (
            <div key={p.id} className="jj-card p-5 flex gap-4" data-testid={`my-post-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-jjblue/90 whitespace-pre-wrap line-clamp-3">{p.content}</p>
                <div className="mt-3 flex gap-4 text-xs text-[var(--jj-muted)]">
                  <span>{new Date(p.created_at).toLocaleString()}</span>
                  <span>· {p.comments_count} comment{p.comments_count === 1 ? "" : "s"}</span>
                  {p.media_paths?.length > 0 && <span>· {p.media_paths.length} attachment{p.media_paths.length === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <button onClick={() => del("posts", p.id, "posts")} aria-label="Delete post"
                className="text-[var(--jj-muted)] hover:text-burgundy p-2 self-start"
                data-testid={`my-post-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          )))}

          {data && tab === "prayers" && (list.length === 0 ? <Empty label="Prayer requests" /> : list.map((p) => (
            <div key={p.id} className="jj-card p-5 flex gap-4" data-testid={`my-prayer-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-jjblue/90 whitespace-pre-wrap line-clamp-3">{p.content}</p>
                <div className="mt-3 flex gap-4 text-xs text-[var(--jj-muted)]">
                  <span>{new Date(p.created_at).toLocaleString()}</span>
                  <span>· {p.praying_count} interceding</span>
                  {p.group_id && <Link to={`/groups/${p.group_id}`} className="text-burgundy hover:underline">View group</Link>}
                </div>
              </div>
              <button onClick={() => del("prayers", p.id, "prayers")} aria-label="Delete prayer request"
                className="text-[var(--jj-muted)] hover:text-burgundy p-2 self-start"
                data-testid={`my-prayer-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          )))}

          {data && tab === "memories" && (list.length === 0 ? <Empty label="Memories" /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {list.map((m) => (
                <div key={m.id} className="jj-card overflow-hidden relative group" data-testid={`my-memory-${m.id}`}>
                  <MediaView path={m.media_path} kind={m.media_kind} className="w-full h-32 object-cover" />
                  {m.caption && <div className="p-3 text-sm text-jjblue/85 line-clamp-2">{m.caption}</div>}
                  <button onClick={() => del("memories", m.id, "memories")} aria-label="Delete memory"
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 text-[var(--jj-muted)] hover:text-burgundy opacity-0 group-hover:opacity-100 transition"
                    data-testid={`my-memory-delete-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          ))}

          {data && tab === "testimonies" && (list.length === 0 ? <Empty label="Testimonies" /> : list.map((t) => (
            <div key={t.id} className="jj-card p-5 flex gap-4" data-testid={`my-testimony-${t.id}`}>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl text-jjblue">{t.title}</h3>
                <p className="text-jjblue/85 mt-1 line-clamp-2">{t.content}</p>
                <div className="mt-2 text-xs text-[var(--jj-muted)]">{new Date(t.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => del("testimonies", t.id, "testimonies")} aria-label="Delete testimony"
                className="text-[var(--jj-muted)] hover:text-burgundy p-2 self-start"
                data-testid={`my-testimony-delete-${t.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          )))}

          {data && tab === "comments" && (list.length === 0 ? <Empty label="Comments" /> : list.map((c) => (
            <div key={c.id} className="jj-card p-5 flex gap-4" data-testid={`my-comment-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-jjblue/90 line-clamp-3">"{c.content}"</p>
                <div className="mt-2 text-xs text-[var(--jj-muted)]">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => del("comments", c.id, "comments")} aria-label="Delete comment"
                className="text-[var(--jj-muted)] hover:text-burgundy p-2 self-start"
                data-testid={`my-comment-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          )))}
        </div>
      </div>
    </div>
  );
}
